import React, { useRef, useState } from 'react';
import { FiX } from 'react-icons/fi';
import api, { StatementsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { LoaderInline, Spinner } from './Loader';

const colors = {
  stone100: '#f5f5f4',
  stone200: '#e7e5e4',
  stone500: '#78716c',
  stone700: '#44403c',
  stone800: '#292524',
  blue600: '#2563eb'
};

function inferFileType(file) {
  const explicitType = String(file?.type || '').trim().toLowerCase();
  if (explicitType) return explicitType;
  const name = String(file?.name || '').toLowerCase();
  if (name.endsWith('.pdf')) return 'application/pdf';
  if (name.endsWith('.csv')) return 'text/csv';
  if (name.endsWith('.xlsx')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (name.endsWith('.xls')) return 'application/vnd.ms-excel';
  return 'application/octet-stream';
}

async function uploadViaPresigned(file, dir) {
  const resolvedFileType = inferFileType(file);
  const { data: presigned } = await api.get('/api/upload/presigned-url', {
    params: { fileType: resolvedFileType, dir }
  });
  await fetch(presigned.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': resolvedFileType },
    body: file
  });
  const key = new URL(presigned.fileUrl).pathname.replace(/^\//, '');
  return { fileUrl: presigned.fileUrl, s3Key: key };
}

async function sha256File(file) {
  const arr = await file.arrayBuffer();
  const digest = await window.crypto.subtle.digest('SHA-256', arr);
  const bytes = new Uint8Array(digest);
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function countPdfPages(file) {
  if (!file?.name?.toLowerCase().endsWith('.pdf')) return 1;
  try {
    const text = await file.text();
    const matches = text.match(/\/Type\s*\/Page[^s]/g);
    return matches ? matches.length : 1;
  } catch (_) {
    return 1;
  }
}

export default function StatementUploadModal({ isOpen, onClose, onUploaded, onRunMatching }) {
  const { company } = useAuth();
  const companyId = company?.id || null;
  const inputRef = useRef(null);

  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [statement, setStatement] = useState(null);
  const [uploadedStatements, setUploadedStatements] = useState([]);
  const [runningMatch, setRunningMatch] = useState(false);
  const [runMessage, setRunMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState('');

  const fileKey = (f) => `${String(f?.name || '')}:${Number(f?.size || 0)}:${Number(f?.lastModified || 0)}`;

  const doUpload = async () => {
    if (!companyId || uploading) return;
    const list = Array.isArray(files) ? files : [];
    if (list.length === 0) return;
    setRunMessage('');
    setUploadProgress('');
    setUploading(true);
    try {
      const dir = `statements/${companyId}`;
      const uploadedList = [];
      const metas = await Promise.all(list.map(async (f) => ({
        file: f,
        id: fileKey(f),
        fileSha256: await sha256File(f),
        pageCount: await countPdfPages(f)
      })));
      const dupResp = await StatementsAPI.checkDuplicates(metas.map((m) => m.fileSha256));
      const dupMap = dupResp?.duplicates || {};
      const uploadable = [];
      for (let i = 0; i < metas.length; i += 1) {
        const meta = metas[i];
        const duplicate = dupMap[String(meta.fileSha256 || '').toLowerCase()];
        if (duplicate) {
          setUploadProgress(`Skipped duplicate ${i + 1}/${metas.length}: ${meta.file?.name || 'file'}.`);
          continue;
        }
        if (meta.pageCount > 40) {
          setUploadProgress(`Skipped ${i + 1}/${metas.length}: ${meta.file?.name || 'file'} (too many pages).`);
          continue;
        }
        uploadable.push(meta);
      }

      for (let i = 0; i < uploadable.length; i += 1) {
        const f = uploadable[i].file;
        const meta = uploadable[i];
        const resolvedFileType = inferFileType(f);
        setUploadProgress(`Uploading ${i + 1}/${uploadable.length}: ${f?.name || 'file'}...`);
        // eslint-disable-next-line no-await-in-loop
        const up = await uploadViaPresigned(f, dir);
        // eslint-disable-next-line no-await-in-loop
        const uploaded = await StatementsAPI.upload({
          fileUrl: up.fileUrl,
          s3Key: up.s3Key,
          fileType: resolvedFileType,
          name: f.name,
          type: 'BANK_STATEMENT',
          fileSha256: meta.fileSha256,
          pageCount: meta.pageCount
        });
        uploadedList.push(uploaded);
        setStatement(uploaded);
        setUploadedStatements((prev) => [...(Array.isArray(prev) ? prev : []), uploaded]);
        setFiles((prev) => {
          const arr = Array.isArray(prev) ? prev : [];
          const k = fileKey(f);
          return arr.filter((pf) => fileKey(pf) !== k);
        });
        if (onUploaded) onUploaded(uploaded);
      }
      setUploadProgress(`Uploaded ${uploadedList.length} statement(s).`);
    } catch (err) {
      window.alert(err?.response?.data?.error || err.message || 'Statement upload failed');
    }
    setUploading(false);
  };

  const runMatching = async () => {
    if (runningMatch) return;
    const list = (Array.isArray(uploadedStatements) && uploadedStatements.length > 0)
      ? uploadedStatements
      : (statement?.id ? [statement] : []);
    if (list.length === 0) {
      setRunMessage('Upload & parse a statement first.');
      window.alert('Please click "Upload & Parse" before running matching.');
      return;
    }
    setRunMessage('');
    setRunningMatch(true);
    try {
      const totals = { matched: 0, suggested: 0, unmatched: 0 };
      for (let i = 0; i < list.length; i += 1) {
        const st = list[i];
        if (!st?.id) continue;
        setRunMessage(`Running matching ${i + 1}/${list.length}: ${st?.name || `Statement #${st.id}`}`);
        // eslint-disable-next-line no-await-in-loop
        const res = await StatementsAPI.runMatch(st.id);
        const next = res?.statement || st;
        setStatement(next);
        if (onRunMatching) onRunMatching(next);
        totals.matched += Number(res?.summary?.matched || 0);
        totals.suggested += Number(res?.summary?.suggested || 0);
        totals.unmatched += Number(res?.summary?.unmatched || 0);
      }
      setRunMessage(`Matching complete: ${totals.matched} high-confidence, ${totals.suggested} suggested, ${totals.unmatched} unmatched.`);
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Matching failed';
      setRunMessage(msg);
      window.alert(msg);
    }
    setRunningMatch(false);
  };

  if (!isOpen) return null;

  const txns = Array.isArray(statement?.transactions) ? statement.transactions : [];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => !uploading && !runningMatch && onClose?.()}>
      <div style={{ width: 960, maxWidth: '96vw', maxHeight: '90vh', overflow: 'auto', background: '#fff', borderRadius: 12, border: `1px solid ${colors.stone200}` }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: 14, borderBottom: `1px solid ${colors.stone200}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, color: colors.stone800 }}>Upload Bank/Card Statement</h3>
          <button className="btn btn-secondary" onClick={() => !uploading && !runningMatch && onClose?.()}>Close</button>
        </div>

        <div style={{ padding: 14 }}>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept=".pdf,.csv,.xlsx,.xls,application/pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            style={{ display: 'none' }}
            onChange={(e) => {
              const nextFiles = Array.from(e.target.files || []);
              if (nextFiles.length > 0) {
                setFiles((prev) => {
                  const prevArr = Array.isArray(prev) ? prev : [];
                  const seen = new Set(prevArr.map((f) => fileKey(f)));
                  const merged = [...prevArr];
                  for (const f of nextFiles) {
                    const k = fileKey(f);
                    if (!seen.has(k)) {
                      seen.add(k);
                      merged.push(f);
                    }
                  }
                  return merged;
                });
              }
              setRunMessage('');
              e.target.value = '';
            }}
          />

          <div style={{ border: `2px dashed ${colors.stone200}`, borderRadius: 12, padding: 24, textAlign: 'center', background: colors.stone100 }}>
            <div style={{ fontSize: 14, color: colors.stone700 }}>Supported formats: PDF, CSV, XLSX</div>
            <button className="btn btn-secondary" style={{ marginTop: 10 }} onClick={() => inputRef.current?.click()} disabled={uploading || runningMatch}>
              Choose Statement
            </button>
            {files.length > 0 && (
              <div style={{ marginTop: 10, fontSize: 13, color: colors.stone800 }}>
                Selected: <b>{files.length}</b> file(s)
                <div style={{ marginTop: 6, fontSize: 12, color: colors.stone700 }}>
                  {files.map((f) => (
                    <div key={fileKey(f)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                      <button
                        type="button"
                        title="Remove file"
                        aria-label={`Remove ${f.name}`}
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: 999,
                          border: `1px solid ${colors.stone200}`,
                          background: '#fff',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          color: colors.stone700,
                          flex: '0 0 auto'
                        }}
                        disabled={uploading || runningMatch}
                        onClick={() => {
                          const k = fileKey(f);
                          setFiles((prev) => (Array.isArray(prev) ? prev.filter((pf) => fileKey(pf) !== k) : []));
                        }}
                      >
                        <FiX size={14} />
                      </button>
                    </div>
                  ))}
                  <div style={{ marginTop: 6, display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ padding: '2px 8px', fontSize: 12 }}
                      disabled={uploading || runningMatch}
                      onClick={() => setFiles([])}
                    >
                      Clear files
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {uploadProgress && (
            <div style={{ marginTop: 10, fontSize: 12, color: colors.stone700, fontWeight: 600 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Spinner size={13} color={colors.blue600} />
                <span>{uploadProgress}</span>
              </span>
            </div>
          )}

          {uploadedStatements.length > 1 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12, color: colors.stone500, marginBottom: 8 }}>
                Uploaded statements ({uploadedStatements.length}). Click one to preview.
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {uploadedStatements.map((st) => {
                  const active = Number(st?.id || 0) === Number(statement?.id || 0);
                  const label = `${st?.bankName || 'Bank'} • ${st?.name || `Statement #${st?.id || ''}`}`;
                  return (
                    <button
                      key={st.id}
                      type="button"
                      onClick={() => setStatement(st)}
                      disabled={uploading || runningMatch}
                      title={label}
                      style={{
                        border: `1px solid ${active ? colors.stone800 : colors.stone200}`,
                        borderRadius: 10,
                        padding: '6px 10px',
                        fontSize: 12,
                        background: active ? colors.stone100 : '#fff',
                        color: colors.stone800,
                        cursor: 'pointer',
                        maxWidth: 360,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {st?.bankName || 'Bank'} • {st?.name || `Statement #${st?.id || ''}`}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {statement && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12, color: colors.stone500, marginBottom: 6 }}>
                {uploadedStatements.length > 1 ? `Previewing ${Math.max(1, uploadedStatements.findIndex((s) => Number(s?.id || 0) === Number(statement?.id || 0)) + 1)} of ${uploadedStatements.length} • ` : ''}
                Parsed {txns.length} transactions | {statement.bankName || 'Bank N/A'} | {statement.startDate || '-'} to {statement.endDate || '-'}
              </div>
              <div style={{ border: `1px solid ${colors.stone200}`, borderRadius: 10, overflow: 'auto', maxHeight: 320 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: 8, borderBottom: `1px solid ${colors.stone200}` }}>Date</th>
                      <th style={{ textAlign: 'left', padding: 8, borderBottom: `1px solid ${colors.stone200}` }}>Description</th>
                      <th style={{ textAlign: 'right', padding: 8, borderBottom: `1px solid ${colors.stone200}` }}>Debit</th>
                      <th style={{ textAlign: 'right', padding: 8, borderBottom: `1px solid ${colors.stone200}` }}>Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txns.slice(0, 50).map((t) => (
                      <tr key={t.id}>
                        <td style={{ padding: 8, borderBottom: `1px solid ${colors.stone200}` }}>{t.date || '-'}</td>
                        <td style={{ padding: 8, borderBottom: `1px solid ${colors.stone200}` }}>{t.description || '-'}</td>
                        <td style={{ padding: 8, borderBottom: `1px solid ${colors.stone200}`, textAlign: 'right' }}>{Number(t.debit || 0) > 0 ? Number(t.debit).toFixed(2) : '-'}</td>
                        <td style={{ padding: 8, borderBottom: `1px solid ${colors.stone200}`, textAlign: 'right' }}>{Number(t.credit || 0) > 0 ? Number(t.credit).toFixed(2) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {txns.length > 50 && (
                <div style={{ marginTop: 6, fontSize: 12, color: colors.stone500 }}>Showing first 50 rows</div>
              )}
            </div>
          )}
          {runMessage && (
            <div style={{ marginTop: 10, fontSize: 12, color: colors.blue600, fontWeight: 600 }}>
              {(uploading || runningMatch) ? <LoaderInline label={runMessage} color={colors.blue600} /> : runMessage}
            </div>
          )}
        </div>

        <div style={{ padding: 14, borderTop: `1px solid ${colors.stone200}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => !uploading && !runningMatch && onClose?.()} disabled={uploading || runningMatch}>Cancel</button>
          <button className="btn btn-secondary" onClick={doUpload} disabled={files.length === 0 || uploading || runningMatch}>
            {uploading ? <LoaderInline label="Uploading" color={colors.stone800} /> : 'Upload & Parse'}
          </button>
          <button className="submit-button" onClick={runMatching} disabled={(!statement?.id && uploadedStatements.length === 0) || uploading || runningMatch}>
            {runningMatch ? <LoaderInline label="Matching" color="#fff" /> : 'Run Matching'}
          </button>
        </div>
      </div>
    </div>
  );
}
